import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, KeyRound, MessagesSquare, Plus, Send, Trash2, Webhook } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Switch } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

type EventDef = { key: string; label: string };
type Chat = { id: string; provider: 'SLACK' | 'TEAMS' | 'GOOGLE_CHAT'; name: string; events: string[]; isActive: boolean; lastStatus: string | null; lastSentAt: string | null; webhookHint: string };
type Hook = {
  id: string;
  url: string;
  events: string[];
  description: string | null;
  isActive: boolean;
  createdAt: string;
  deliveries: { id: string; event: string; status: string; attempts: number; responseCode: number | null; error: string | null; createdAt: string }[];
};
type Key = { id: string; name: string; prefix: string; readOnly: boolean; lastUsedAt: string | null; expiresAt: string | null; createdAt: string };

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);
const PROVIDERS = { SLACK: 'Slack', TEAMS: 'Microsoft Teams', GOOGLE_CHAT: 'Google Chat' } as const;

/**
 * Settings → Integrations: post to Slack / Teams / Google Chat, send signed
 * webhooks to Zapier, Make or your own systems, and API keys for the REST API.
 */
export function IntegrationsPage() {
  const t = useT();
  const [tab, setTab] = useState<'chat' | 'webhooks' | 'keys'>('chat');
  return (
    <div>
      <PageHeader title="Integrations" description="Connect DovoCRM to the tools your team already uses." />
      <div className="mb-4 flex gap-1 rounded-lg border bg-card p-1">
        {(
          [
            ['chat', 'Team chat', MessagesSquare],
            ['webhooks', 'Webhooks', Webhook],
            ['keys', 'API keys', KeyRound],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn('flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-sm font-semibold', tab === key ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'text-muted-foreground hover:bg-muted')}
          >
            <Icon className="h-4 w-4" /> {t(label)}
          </button>
        ))}
      </div>
      {tab === 'chat' && <ChatTab />}
      {tab === 'webhooks' && <WebhooksTab />}
      {tab === 'keys' && <KeysTab />}
    </div>
  );
}

function useEvents() {
  return useQuery({ queryKey: ['integrations', 'events'], queryFn: () => apiGet<EventDef[]>('/integrations/events'), staleTime: Infinity });
}

function EventPicker({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const t = useT();
  const { data: events = [] } = useEvents();
  return (
    <div className="grid gap-1.5 sm:grid-cols-2">
      {events.map((event) => (
        <label key={event.key} className="flex items-center gap-2 rounded border px-2.5 py-1.5 text-sm">
          <Checkbox checked={value.includes(event.key)} onCheckedChange={(checked) => onChange(checked ? [...value, event.key] : value.filter((key) => key !== event.key))} />
          {t(event.label)}
        </label>
      ))}
    </div>
  );
}

// ── Team chat ────────────────────────────────────────────────────────────────

function ChatTab() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: chats = [] } = useQuery({ queryKey: ['integrations', 'chat'], queryFn: () => apiGet<Chat[]>('/integrations/chat') });
  const [adding, setAdding] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['integrations', 'chat'] });
  const test = useMutation({
    mutationFn: (id: string) => apiPost<{ ok: boolean; status: string }>(`/integrations/chat/${id}/test`),
    onSuccess: (result) => {
      if (result.ok) toast.success(t('Test message sent'));
      else toast.error(result.status);
      refresh();
    },
  });
  const toggle = useMutation({ mutationFn: (chat: Chat) => apiPatch(`/integrations/chat/${chat.id}`, { isActive: !chat.isActive }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => apiDelete(`/integrations/chat/${id}`), onSuccess: refresh });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-lg border bg-card p-4">
        <p className="text-sm text-muted-foreground">{t('New leads, won deals, tickets and payments posted straight into a channel.')}</p>
        <Can permission="admin.integration.create">
          <Button onClick={() => setAdding(true)}>
            <Plus /> {t('Connect a channel')}
          </Button>
        </Can>
      </div>
      {chats.map((chat) => (
        <div key={chat.id} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-4">
          <Badge variant="secondary">{PROVIDERS[chat.provider]}</Badge>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{chat.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {chat.events.length} {t('events')} · {chat.webhookHint}
              {chat.lastSentAt && ` · ${t('last')} ${timeAgo(chat.lastSentAt)} (${chat.lastStatus})`}
            </p>
          </div>
          <Switch checked={chat.isActive} onCheckedChange={() => toggle.mutate(chat)} />
          <Button size="sm" variant="outline" onClick={() => test.mutate(chat.id)} disabled={test.isPending}>
            <Send /> {t('Test')}
          </Button>
          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => window.confirm(t('Disconnect this channel?')) && remove.mutate(chat.id)}>
            <Trash2 />
          </Button>
        </div>
      ))}
      {adding && <ChatDialog onClose={() => setAdding(false)} onSaved={refresh} />}
    </div>
  );
}

function ChatDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const [form, setForm] = useState({ provider: 'SLACK' as Chat['provider'], name: '', webhookUrl: '', events: ['lead.created', 'deal.won', 'ticket.created', 'payment.received'] });
  const save = useMutation({
    mutationFn: () => apiPost('/integrations/chat', form),
    onSuccess: () => {
      onSaved();
      onClose();
      toast.success(t('Channel connected'));
    },
    onError: (error) => failed(error, 'Could not connect the channel'),
  });
  const help: Record<Chat['provider'], string> = {
    SLACK: 'In Slack: Apps → Incoming Webhooks → Add to Slack, pick the channel, copy the Webhook URL.',
    TEAMS: 'In Teams: the channel ⋯ → Workflows → "Post to a channel when a webhook request is received", copy the URL.',
    GOOGLE_CHAT: 'In Google Chat: the space → Apps & integrations → Webhooks → Add, copy the URL.',
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Connect a channel')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Select value={form.provider} onChange={(event) => setForm({ ...form, provider: event.target.value as Chat['provider'] })}>
            {Object.entries(PROVIDERS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
          <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">{t(help[form.provider])}</p>
          <div className="space-y-1.5">
            <Label>{t('Name')}</Label>
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="#sales-wins" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Webhook URL')}</Label>
            <Input value={form.webhookUrl} onChange={(event) => setForm({ ...form, webhookUrl: event.target.value })} placeholder="https://hooks.slack.com/services/…" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Post these events')}</Label>
            <EventPicker value={form.events} onChange={(events) => setForm({ ...form, events })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!form.name || !form.webhookUrl || !form.events.length || save.isPending}>
            {t('Connect')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Webhooks ─────────────────────────────────────────────────────────────────

function WebhooksTab() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: hooks = [] } = useQuery({ queryKey: ['integrations', 'webhooks'], queryFn: () => apiGet<Hook[]>('/integrations/webhooks') });
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['integrations', 'webhooks'] });
  const test = useMutation({
    mutationFn: (id: string) => apiPost<{ status: string; responseCode: number | null; error: string | null }>(`/integrations/webhooks/${id}/test`),
    onSuccess: (result) => {
      if (result.status === 'DELIVERED') toast.success(t('Delivered ({code})', { code: result.responseCode ?? '' }));
      else toast.error(result.error ?? t('Not delivered'));
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => apiDelete(`/integrations/webhooks/${id}`), onSuccess: refresh });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4">
        <p className="max-w-2xl text-sm text-muted-foreground">
          {t('Events are POSTed as JSON. Check the X-Dovo-Signature header: sha256 HMAC of "timestamp.body" with your signing secret. Failed deliveries are retried for 12 hours.')}
        </p>
        <Can permission="admin.integration.create">
          <Button onClick={() => setAdding(true)}>
            <Plus /> {t('Add webhook')}
          </Button>
        </Can>
      </div>
      {secret && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:bg-amber-500/10">
          <p className="font-semibold">{t('Signing secret — copy it now, it is shown only once')}</p>
          <SecretLine value={secret} />
        </div>
      )}
      {hooks.map((hook) => (
        <div key={hook.id} className="rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-sm font-semibold">{hook.url}</p>
              <p className="text-xs text-muted-foreground">{hook.events.join(', ')}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => test.mutate(hook.id)} disabled={test.isPending}>
              <Send /> {t('Send test')}
            </Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => window.confirm(t('Delete this webhook?')) && remove.mutate(hook.id)}>
              <Trash2 />
            </Button>
          </div>
          {hook.deliveries.length > 0 && (
            <ul className="mt-3 space-y-1 border-t pt-3 text-xs">
              {hook.deliveries.map((delivery) => (
                <li key={delivery.id} className="flex items-center gap-2">
                  <span className={cn('h-2 w-2 rounded-full', delivery.status === 'DELIVERED' ? 'bg-emerald-500' : delivery.status === 'FAILED' ? 'bg-red-500' : 'bg-amber-400')} />
                  <span className="font-mono">{delivery.event}</span>
                  <span className="text-muted-foreground">
                    {delivery.status.toLowerCase()} {delivery.responseCode ? `· ${delivery.responseCode}` : ''} {delivery.error ? `· ${delivery.error}` : ''} · {timeAgo(delivery.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      {adding && (
        <WebhookDialog
          onClose={() => setAdding(false)}
          onSaved={(value) => {
            setSecret(value);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function WebhookDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (secret: string) => void }) {
  const t = useT();
  const [form, setForm] = useState({ url: '', description: '', events: ['lead.created', 'deal.won'] });
  const save = useMutation({
    mutationFn: () => apiPost<{ id: string; secret: string }>('/integrations/webhooks', form),
    onSuccess: (result) => {
      onSaved(result.secret);
      onClose();
    },
    onError: (error) => failed(error, 'Could not add the webhook'),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Add webhook')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('Endpoint URL (https)')}</Label>
            <Input value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} placeholder="https://hooks.zapier.com/hooks/catch/…" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Description')}</Label>
            <Input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder={t('e.g. Zapier → Google Sheets')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Events')}</Label>
            <EventPicker value={form.events} onChange={(events) => setForm({ ...form, events })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!form.url || !form.events.length || save.isPending}>
            {t('Add webhook')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── API keys ─────────────────────────────────────────────────────────────────

function SecretLine({ value }: { value: string }) {
  const t = useT();
  return (
    <div className="mt-2 flex items-center gap-2">
      <code className="min-w-0 flex-1 break-all rounded bg-white px-2 py-1.5 font-mono text-xs dark:bg-black/30">{value}</code>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          toast.success(t('Copied'));
        }}
      >
        <Copy />
      </Button>
    </div>
  );
}

function KeysTab() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: keys = [] } = useQuery({ queryKey: ['integrations', 'keys'], queryFn: () => apiGet<Key[]>('/integrations/api-keys') });
  const [form, setForm] = useState({ name: '', readOnly: false });
  const [created, setCreated] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['integrations', 'keys'] });
  const create = useMutation({
    mutationFn: () => apiPost<{ key: string }>('/integrations/api-keys', form),
    onSuccess: (result) => {
      setCreated(result.key);
      setForm({ name: '', readOnly: false });
      refresh();
    },
    onError: (error) => failed(error, 'Could not create the key'),
  });
  const revoke = useMutation({ mutationFn: (id: string) => apiDelete(`/integrations/api-keys/${id}`), onSuccess: refresh });
  const origin = typeof window === 'undefined' ? 'https://dovocrm.com' : window.location.origin;
  return (
    <div className="space-y-3">
      <div className="rounded-lg border bg-card p-4 text-sm">
        <p className="font-semibold">{t('REST API')}</p>
        <p className="mt-1 text-muted-foreground">{t('Send the key in the X-API-Key header. A key acts as you, with your permissions (never admin settings). Read-only keys can only read and export.')}</p>
        <code className="mt-2 block overflow-x-auto rounded bg-muted px-3 py-2 font-mono text-xs">
          curl -H "X-API-Key: dovo_…" {origin}/api/v1/contacts?limit=10
        </code>
      </div>
      <Can permission="admin.integration.create">
        <form
          className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <Input className="max-w-xs" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder={t('Key name, e.g. Zapier')} />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.readOnly} onCheckedChange={(checked) => setForm({ ...form, readOnly: checked === true })} /> {t('Read-only')}
          </label>
          <Button type="submit" disabled={!form.name.trim() || create.isPending}>
            <Plus /> {t('Create key')}
          </Button>
        </form>
      </Can>
      {created && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:bg-amber-500/10">
          <p className="font-semibold">{t('Your new API key — copy it now, it is shown only once')}</p>
          <SecretLine value={created} />
        </div>
      )}
      <div className="overflow-hidden rounded-lg border bg-card">
        {keys.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('No API keys yet.')}</p>
        ) : (
          keys.map((key) => (
            <div key={key.id} className="flex flex-wrap items-center gap-3 border-b px-4 py-3 text-sm last:border-0">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold">{key.name}</span>
              <code className="text-xs text-muted-foreground">{key.prefix}…</code>
              {key.readOnly && <Badge variant="secondary">{t('Read-only')}</Badge>}
              <span className="ms-auto text-xs text-muted-foreground">
                {key.lastUsedAt ? `${t('Used')} ${timeAgo(key.lastUsedAt)}` : t('Never used')} · {t('Created')} {formatDate(key.createdAt)}
              </span>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => window.confirm(t('Revoke this key? Anything using it stops working.')) && revoke.mutate(key.id)}>
                {t('Revoke')}
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
